import { GammaAPIService } from "@/service/gamma-api.service";
import { OcrApiService } from "@/service/ocr-api.service";
import type {
  ApplyRecommendationsReport,
  ApplyRecommendationsRequest,
  BrpInput,
  Loan,
  LoanOption,
  MortgageOption,
  RecommendationGroup,
  RecommendationSelected
} from "@/types";
import type { HouseTypology, SeaiPartialBer } from "@/types/selections";
import type { PassportPlanStep } from "@/store/passport";
import type { RoadmapStrategy } from "@/helpers";

type StageSnapshot = {
  step: PassportPlanStep;
  report: ApplyRecommendationsReport;
};

type GenerateBrpReportParams = {
  berwowId?: string;
  recommendationGroups: RecommendationGroup[];
  planYearSteps: PassportPlanStep[];
  roadmap: RoadmapStrategy[];
  selectedStrategyId?: string;
  loanOptions: LoanOption[];
  selectedLoan?: Loan;
  propertyValue?: number;
  mortgageActive?: boolean;
  mortgage?: MortgageOption;
  addressInfo?: {
    label?: string;
    lines?: Array<{ key: string; value: string }>;
    county?: string;
    postCode?: string;
  };
  addressResult?: {
    data?: {
      location?: {
        latitude?: number;
        longitude?: number;
      };
    };
  };
  berDetails?: SeaiPartialBer;
  houseTypology?: HouseTypology;
  storeys?: number;
  age?: number;
};

const DEFAULT_PDF_CREATE_REQUEST = {
  template: "home-upgrade-roadmap.html",
  parameters: {
    title: "Report",
    content: "This is an example.",
    generatedAt: "2026-03-18T00:00:00Z",
    extra: { foo: "bar" }
  }
} as const;

type StageMetrics = {
  globalEPNRconsumption: number;
  letterEPNRconsumption: string;
  globalDemand: number;
  globalCO2emissions: number;
  letterCO2emissions: string;
  energyBillGlobal: number;
};

const DEFAULT_COUNTRY = "IT";
const DEFAULT_CURRENT_YEAR = 2025;
const DEFAULT_OBJECTIVE_YEAR = 2050;
const DEFAULT_LATITUDE = 53.34391;
const DEFAULT_LONGITUDE = -6.2721;
const DEFAULT_N_UNITS = 1;
const DEFAULT_STAGE_LIFESPAN_YEARS = 20;
const DEFAULT_PROPERTY_VALUE_EURO_SM2 = 2800;
const DEFAULT_YEAR_RENOVATION = 2008;
const DEFAULT_YEAR_CHANGE_HVAC = 2012;

function isApplyRecommendationsReport(
  value: ApplyRecommendationsReport | { exceptionType?: string } | undefined
): value is ApplyRecommendationsReport {
  return !!value && !("exceptionType" in value);
}

function toNumber(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeRatingLetter(value: unknown) {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-G]/g, "")
    .charAt(0);
}

function getSelectedRecommendationIds(groups: RecommendationGroup[]) {
  return new Set(
    groups
      .filter((group) => group.isSelected)
      .flatMap((group) => group.recommendations)
      .filter((recommendation) => recommendation.isSelected)
      .map((recommendation) => recommendation.id)
  );
}

function getUserAlreadyAppliedIds(groups: RecommendationGroup[]) {
  return new Set(
    groups
      .flatMap((group) => group.recommendations)
      .filter((recommendation) => recommendation.isUpgraded)
      .map((recommendation) => recommendation.id)
  );
}

function getStepRecommendationIds(step: PassportPlanStep) {
  return Array.from(
    new Set(
      step.measures
        .filter((measure) => measure.included !== false)
        .flatMap((measure) => measure.recommendationIds ?? [])
    )
  );
}

function getRecommendationIndex(groups: RecommendationGroup[]) {
  return new Map(
    groups
      .flatMap((group) => group.recommendations)
      .map((recommendation) => [recommendation.id, recommendation] as const)
  );
}

function buildApplyRecommendationsRequest(
  params: GenerateBrpReportParams,
  selectedRecommendationIds: string[],
  alreadyAppliedIds: Set<string>
): ApplyRecommendationsRequest {
  const recommendationsSelected: RecommendationSelected[] = selectedRecommendationIds.map(
    (recommendationId) => ({
      RecommendationId: recommendationId,
      AlreadyUpgraded: alreadyAppliedIds.has(recommendationId)
    })
  );

  const request: ApplyRecommendationsRequest = {
    BerWowId: params.berwowId,
    RecommendationsSelected: recommendationsSelected,
    LoanOptions: params.loanOptions,
    LoanOptionSelected: params.selectedLoan,
    PropertyValue: params.propertyValue ?? 0
  };

  if (params.mortgageActive && params.mortgage) {
    request.MortgageOptionSelected = params.mortgage;
  }

  return request;
}

async function getStageSnapshots(
  params: GenerateBrpReportParams
): Promise<StageSnapshot[]> {
  const selectedRecommendationIds = getSelectedRecommendationIds(params.recommendationGroups);
  const alreadyAppliedIds = getUserAlreadyAppliedIds(params.recommendationGroups);
  const cumulativeAlreadyAppliedIds = new Set(alreadyAppliedIds);
  const stageRequests: Array<{
    step: PassportPlanStep;
    request: ApplyRecommendationsRequest;
  }> = [];

  for (const step of params.planYearSteps) {
    const currentStepIds = getStepRecommendationIds(step).filter((id) =>
      selectedRecommendationIds.has(id)
    );

    if (!currentStepIds.length) continue;

    const request = buildApplyRecommendationsRequest(
      params,
      Array.from(new Set([...cumulativeAlreadyAppliedIds, ...currentStepIds])),
      cumulativeAlreadyAppliedIds
    );

    stageRequests.push({
      step,
      request
    });

    currentStepIds.forEach((id) => cumulativeAlreadyAppliedIds.add(id));
  }

  const reports = await Promise.all(
    stageRequests.map(async ({ request }) => {
      const report = await GammaAPIService.applyRecommendations(request);
      if (!isApplyRecommendationsReport(report)) {
        throw new Error("Failed to generate stage report for BRP");
      }

      return report;
    })
  );

  return stageRequests.map(({ step }, index) => ({
    step,
    report: reports[index]
  }));
}

function getStageMetrics(
  report: ApplyRecommendationsReport,
  phase: "before" | "after"
): StageMetrics {
  const analysis = report.resultsAnalysis[phase];
  const rating =
    phase === "before" ? report.beforeRating?.code : report.afterRating?.code;
  const co2 =
    phase === "before"
      ? report.carbon?.co2EmissionsBefore
      : report.carbon?.co2EmissionsAfter;
  const annualCost =
    phase === "before" ? report.annualCostBefore : report.annualCostAfter;

  return {
    globalEPNRconsumption: toNumber(analysis?.energy),
    letterEPNRconsumption: normalizeRatingLetter(rating),
    globalDemand: toNumber(analysis?.heatLossIndicator),
    globalCO2emissions: toNumber(co2) / 1000,
    letterCO2emissions: normalizeRatingLetter(rating),
    energyBillGlobal: toNumber(annualCost)
  };
}

function getStageDurationYears(
  stageYears: number[],
  stageIndex: number,
  objectiveYear: number
) {
  const currentYear = stageYears[stageIndex];
  const nextYear = stageYears[stageIndex + 1] ?? objectiveYear;
  return Math.max(1, nextYear - currentYear);
}

function buildPlanDurations(stageYears: number[], objectiveYear: number): BrpInput["plan"] {
  return stageYears.reduce((durations, _year, index) => {
    durations[`durationStage${index}`] = getStageDurationYears(
      stageYears,
      index,
      objectiveYear
    );
    return durations;
  }, {} as BrpInput["plan"]);
}

function getStepInterventionCost(
  recommendationIds: string[],
  recommendationIndex: Map<string, RecommendationGroup["recommendations"][number]>
) {
  return recommendationIds.reduce(
    (sum, recommendationId) =>
      sum + toNumber(recommendationIndex.get(recommendationId)?.info?.price),
    0
  );
}

function getAddressLabel(params: GenerateBrpReportParams) {
  const lines = params.addressInfo?.lines?.map((line) => line.value).filter(Boolean) ?? [];
  if (lines.length) return lines.join(", ");

  const berAddress = params.berDetails?.address;
  return [
    berAddress?.addressLine1,
    berAddress?.addressLine2,
    berAddress?.addressLine3,
    berAddress?.county,
    berAddress?.postCode
  ]
    .filter(Boolean)
    .join(", ");
}

function getCoordinates(params: GenerateBrpReportParams) {
  const rawLocation = params.addressResult?.data?.location;

  return {
    latitude: toNumber(rawLocation?.latitude, DEFAULT_LATITUDE),
    longitude: toNumber(rawLocation?.longitude, DEFAULT_LONGITUDE)
  };
}

function getYearConstruction(params: GenerateBrpReportParams) {
  const fromBer = toNumber(params.berDetails?.yearOfConstruction, 0);
  if (fromBer > 0) return fromBer;

  const fromAge = toNumber(params.age, 0);
  if (fromAge > 0) return fromAge;

  return DEFAULT_CURRENT_YEAR;
}

function getYearRenovation(yearConstruction: number) {
  return Math.max(yearConstruction, DEFAULT_YEAR_RENOVATION);
}

function getYearChangeHvac(yearConstruction: number) {
  return Math.max(yearConstruction, DEFAULT_YEAR_CHANGE_HVAC);
}

function getBuildingName(params: GenerateBrpReportParams) {
  const addressLabel = getAddressLabel(params);
  if (addressLabel) return addressLabel;

  return params.houseTypology?.description ?? `${DEFAULT_COUNTRY} dwelling`;
}

function getRoofArea(params: GenerateBrpReportParams) {
  const floorArea = toNumber(params.berDetails?.floorArea, 0);
  const floors = Math.max(
    1,
    toNumber(params.houseTypology?.storeys, toNumber(params.storeys, 1))
  );

  if (floorArea > 0) {
    return Math.max(1, Math.round(floorArea / floors));
  }

  return 100;
}

function getOptionalVariables(): BrpInput["optionalVariables"] {
  return {
    ElectricityCost: null,
    FossilFuelCost: null,
    YearlyEnergyCostVariation: null,
    YearlyMoneyValueVariation: null,
    YearlyECMCostVariation: null,
    EPCrevaluationFactor: null,
    MaintenanceFactor: null,
    "ConversionFactorFE-EL-CO2": null,
    "ConversionFactorFE-EL-RPE": null,
    "ConversionFactorFE-EL-NRPE": null,
    "ConversionFactorFE-EL-PE": null,
    "ConversionFactorFE-CFNG-CO2": null,
    "ConversionFactorFE-CFNG-RPE": null,
    "ConversionFactorFE-CFNG-NRPE": null,
    "ConversionFactorFE-CFNG-PE": null,
    Financing: "",
    FinancingPeriod: 1,
    FinancingInterest: 0,
    AvailableGrantsForMeasure: 0,
    LinkToAvailableFunding: "",
    LinkToOSS: ""
  };
}

function buildRpConfigGenPayload(
  params: GenerateBrpReportParams,
  snapshots: StageSnapshot[]
): BrpInput {
  if (!snapshots.length) {
    throw new Error("No BRP stage snapshots available");
  }

  const recommendationIndex = getRecommendationIndex(params.recommendationGroups);
  const strategy = params.roadmap.find((item) => item.id === params.selectedStrategyId);
  const countryCode = DEFAULT_COUNTRY;
  const currentYear = new Date().getFullYear();
  const objectiveYear = strategy?.objectiveYear ?? DEFAULT_OBJECTIVE_YEAR;
  const stepYears = snapshots.map(
    ({ step }, index) => step.year ?? currentYear + index + 1
  );
  const stageYears = [currentYear, ...stepYears];
  const stage0Metrics = getStageMetrics(snapshots[0].report, "before");
  const initialRecommendationIds = Array.from(getUserAlreadyAppliedIds(params.recommendationGroups));
  const { latitude, longitude } = getCoordinates(params);
  const yearConstruction = getYearConstruction(params);
  const nFloors = Math.max(
    1,
    toNumber(params.houseTypology?.storeys, toNumber(params.storeys, 1))
  );
  const scenarios: BrpInput["scenarios"] = [
    {
      scenario_id: `${countryCode}.0`,
      globalME: toNumber(snapshots[0].report.resultsAnalysis.before.energy),
      HeatingME: toNumber(snapshots[0].report.resultsAnalysis.before.shMainEnergy),
      CoolingME: 0,
      DHWME: toNumber(snapshots[0].report.resultsAnalysis.before.whMainEnergy),
      LightingME: toNumber(snapshots[0].report.resultsAnalysis.before.lightingEnergy),
      "globalME-FF": 0,
      "globalME-ELEC": 0,
      "globalME-PV": 0,
      globalFE: toNumber(snapshots[0].report.resultsAnalysis.before.energyDelivered),
      "globalFE-FF": 0,
      "HeatingFE-FF": 0,
      "CoolingFE-FF": 0,
      "DHWFE-FF": 0,
      "LightingFE-FF": 0,
      "globalFE-ELEC": 0,
      "HeatingFE-ELEC": 0,
      "CoolingFE-ELEC": 0,
      "DHWFE-ELEC": 0,
      "LightingFE-ELEC": 0,
      "globalFE-PV": toNumber(snapshots[0].report.resultsAnalysis.before.energyProduced),
      "HeatingFE-PV": 0,
      "CoolingFE-PV": 0,
      "DHWFE-PV": 0,
      "LightingFE-PV": 0,
      globalDemand: stage0Metrics.globalDemand,
      HeatingDemand: toNumber(snapshots[0].report.resultsAnalysis.before.heatLossIndicator),
      CoolingDemand: 0,
      DHWDemand: toNumber(snapshots[0].report.resultsAnalysis.before.whMainEnergy),
      "globalDemand-FF": 0,
      "globalDemand-ELEC": 0,
      "globalDemand-PV": 0,
      globalPE: toNumber(snapshots[0].report.resultsAnalysis.before.energy),
      HeatingPE: toNumber(snapshots[0].report.resultsAnalysis.before.shMainEnergy),
      CoolingPE: 0,
      DHWPE: toNumber(snapshots[0].report.resultsAnalysis.before.whMainEnergy),
      LightingPE: toNumber(snapshots[0].report.resultsAnalysis.before.lightingEnergy),
      globalRPE:
        toNumber(snapshots[0].report.resultsAnalysis.before.renew1Energy) +
        toNumber(snapshots[0].report.resultsAnalysis.before.renew2Energy) +
        toNumber(snapshots[0].report.resultsAnalysis.before.renew3Energy),
      HeatingRPE: 0,
      CoolingRPE: 0,
      DHWRPE: 0,
      LightingRPE: 0,
      globalNRPE: stage0Metrics.globalEPNRconsumption,
      HeatingNRPE: toNumber(snapshots[0].report.resultsAnalysis.before.shMainEnergy),
      CoolingNRPE: 0,
      DHWNRPE: toNumber(snapshots[0].report.resultsAnalysis.before.whMainEnergy),
      LightingNRPE: toNumber(snapshots[0].report.resultsAnalysis.before.lightingEnergy),
      globalCO2: stage0Metrics.globalCO2emissions,
      HeatingCO2: 0,
      CoolingCO2: 0,
      DHWCO2: 0,
      LightingCO2: 0,
      "globalCO2-ELEC": 0,
      "globalCO2-nonELEC": stage0Metrics.globalCO2emissions,
      globalLetterEPNR: stage0Metrics.letterEPNRconsumption,
      globalLetterCO2: stage0Metrics.letterCO2emissions,
      "GWP-A1A3": 0,
      "GWP-A1A5": 0,
      measures: initialRecommendationIds.map((recommendationId) => ({
        idMeasure: recommendationId,
        type: "NOT-Mant",
        duration_days_unit: 1,
        periodicity_years: DEFAULT_STAGE_LIFESPAN_YEARS
      })),
      status: [],
      maintenaceCost: 0,
      interventionCost: 0
    }
  ];

  snapshots.forEach((snapshot) => {
    const appliedMetrics = getStageMetrics(snapshot.report, "after");
    const stepRecommendationIds = getStepRecommendationIds(snapshot.step);
    const after = snapshot.report.resultsAnalysis.after;

    scenarios.push({
      scenario_id: `${countryCode}.${snapshot.step.stage}`,
      globalME: toNumber(after.energy),
      HeatingME: toNumber(after.shMainEnergy),
      CoolingME: 0,
      DHWME: toNumber(after.whMainEnergy),
      LightingME: toNumber(after.lightingEnergy),
      "globalME-FF": 0,
      "globalME-ELEC": 0,
      "globalME-PV": 0,
      globalFE: toNumber(after.energyDelivered),
      "globalFE-FF": 0,
      "HeatingFE-FF": 0,
      "CoolingFE-FF": 0,
      "DHWFE-FF": 0,
      "LightingFE-FF": 0,
      "globalFE-ELEC": 0,
      "HeatingFE-ELEC": 0,
      "CoolingFE-ELEC": 0,
      "DHWFE-ELEC": 0,
      "LightingFE-ELEC": 0,
      "globalFE-PV": toNumber(after.energyProduced),
      "HeatingFE-PV": 0,
      "CoolingFE-PV": 0,
      "DHWFE-PV": 0,
      "LightingFE-PV": 0,
      globalDemand: appliedMetrics.globalDemand,
      HeatingDemand: toNumber(after.heatLossIndicator),
      CoolingDemand: 0,
      DHWDemand: toNumber(after.whMainEnergy),
      "globalDemand-FF": 0,
      "globalDemand-ELEC": 0,
      "globalDemand-PV": 0,
      globalPE: toNumber(after.energy),
      HeatingPE: toNumber(after.shMainEnergy),
      CoolingPE: 0,
      DHWPE: toNumber(after.whMainEnergy),
      LightingPE: toNumber(after.lightingEnergy),
      globalRPE:
        toNumber(after.renew1Energy) +
        toNumber(after.renew2Energy) +
        toNumber(after.renew3Energy),
      HeatingRPE: 0,
      CoolingRPE: 0,
      DHWRPE: 0,
      LightingRPE: 0,
      globalNRPE: appliedMetrics.globalEPNRconsumption,
      HeatingNRPE: toNumber(after.shMainEnergy),
      CoolingNRPE: 0,
      DHWNRPE: toNumber(after.whMainEnergy),
      LightingNRPE: toNumber(after.lightingEnergy),
      globalCO2: appliedMetrics.globalCO2emissions,
      HeatingCO2: 0,
      CoolingCO2: 0,
      DHWCO2: 0,
      LightingCO2: 0,
      "globalCO2-ELEC": 0,
      "globalCO2-nonELEC": appliedMetrics.globalCO2emissions,
      globalLetterEPNR: appliedMetrics.letterEPNRconsumption,
      globalLetterCO2: appliedMetrics.letterCO2emissions,
      "GWP-A1A3": 0,
      "GWP-A1A5": 0,
      measures: stepRecommendationIds.map((recommendationId) => ({
        idMeasure: recommendationId,
        type: "NOT-Mant",
        duration_days_unit: 1,
        periodicity_years: DEFAULT_STAGE_LIFESPAN_YEARS
      })),
      status: [],
      maintenaceCost: 0,
      interventionCost: getStepInterventionCost(stepRecommendationIds, recommendationIndex)
    });
  });

  return {
    location: {
      latitude,
      longitude
    },
    automatic: {
      country: countryCode,
      year: currentYear
    },
    building: {
      name: getBuildingName(params),
      m2roof: getRoofArea(params),
      nUnits: DEFAULT_N_UNITS,
      nFloors,
      valueEurosm2: DEFAULT_PROPERTY_VALUE_EURO_SM2,
      propertyValueEurom2: DEFAULT_PROPERTY_VALUE_EURO_SM2,
      yearConstruction,
      yearRenovation: getYearRenovation(yearConstruction),
      yearChangeHVAC: getYearChangeHvac(yearConstruction)
    },
    optionalVariables: getOptionalVariables(),
    plan: buildPlanDurations(stageYears, objectiveYear),
    scenarios
  };
}

export class BrpReportService {
  public static async generate(params: GenerateBrpReportParams) {
    const snapshots = await getStageSnapshots(params);
    const request = buildRpConfigGenPayload(params, snapshots);
    const response = await OcrApiService.getRpConfigGen(request);
    const pdfResponse = await GammaAPIService.createPdf({
      template: DEFAULT_PDF_CREATE_REQUEST.template,
      parameters: {
        title: DEFAULT_PDF_CREATE_REQUEST.parameters.title,
        content: DEFAULT_PDF_CREATE_REQUEST.parameters.content,
        generatedAt: DEFAULT_PDF_CREATE_REQUEST.parameters.generatedAt,
        extra: {
          foo: DEFAULT_PDF_CREATE_REQUEST.parameters.extra.foo
        }
      }
    });

    return {
      request,
      response: response?.data ?? response,
      pdfResponse,
      snapshots
    };
  }
}
